import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowLeft, Search, Send, Zap, AlertCircle, Check, X } from 'lucide-react';
import { api } from '../../lib/api';
import { CONTACT_ROLE_LABELS } from '../../lib/outreach';
import { useAuth } from '../../context/AuthContext';
import BackLink from '../../components/admin/BackLink';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import EmptyState from '../../components/crm/EmptyState';

// Sprint K — Enrollment page redesign. Mental model:
//
//   * Campaign status (draft/active/paused/archived) — explicit user action.
//   * Targeting — defines the eligible pool.
//   * Auto-enroll — a setting (cron behaviour). Just a flag. Toggling it
//     does NOT activate the campaign and does NOT enroll anyone immediately.
//   * Enrollment — actual rows. Created either manually (this page),
//     by the auto-enroll cron (when campaign is active + flag is on), or
//     by the explicit "Run enrollment now" button (one-shot backfill).
//
// All three paths (manual / cron / run-now) write the SAME outreach_enrollments
// rows. Once enrolled, an account is enrolled regardless of how it got there.
// "Switching to auto" doesn't undo manual enrollments — they coexist. Users
// who want to clear enrollments do it explicitly via the Remove action on
// the campaign detail page.

const ROLE_LABELS = CONTACT_ROLE_LABELS;
const BOROUGH_LABELS = { M: 'Manhattan', X: 'Bronx', K: 'Brooklyn', Q: 'Queens', R: 'Staten Island' };

export default function OutreachEnroll() {
  const { id } = useParams();
  const { user } = useAuth();
  const isStaff = user?.role === 'nexsav_admin' || user?.role === 'ops';
  const [campaign, setCampaign] = useState(null);
  const [eligibility, setEligibility] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [allAccounts, setAllAccounts] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [enrollResult, setEnrollResult] = useState(null);
  const [enrolling, setEnrolling] = useState(false);
  const [savingAuto, setSavingAuto] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [running, setRunning] = useState(false);
  // Replace-existing prompt for the auto-enroll-on flow. Stored as a
  // pending-state object so the dialog can render its body off real data.
  const [autoEnrollConfirm, setAutoEnrollConfirm] = useState(null);
  const [runNowConfirm, setRunNowConfirm] = useState(false);

  const reload = useCallback(async () => {
    if (!isStaff) { setLoading(false); return; }
    setLoading(true);
    try {
      const [cRes, eRes, enrRes] = await Promise.all([
        api.get(`/api/admin/outreach/campaigns/${id}`),
        api.get(`/api/admin/outreach/campaigns/${id}/eligible-accounts?include_accounts=1&limit=200`),
        api.get(`/api/admin/outreach/campaigns/${id}/enrollments?limit=500`),
      ]);
      setCampaign(cRes.campaign || null);
      setEligibility(eRes || null);
      setEnrollments(enrRes.enrollments || []);
    } finally {
      setLoading(false);
    }
  }, [id, isStaff]);
  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    if (!showAll || allAccounts) return;
    api.get('/api/admin/accounts?limit=500').then((d) => {
      setAllAccounts(d.accounts || []);
    }).catch(() => {});
  }, [showAll, allAccounts]);

  // Fast lookup: which accounts already have an active/paused enrollment?
  // We don't disable accounts whose enrollment is `unsubscribed` or
  // `completed` — those can be re-enrolled if the operator chooses.
  const enrolledAccountIds = useMemo(() => {
    const s = new Set();
    for (const e of enrollments) {
      if (e.status === 'active' || e.status === 'paused') {
        s.add(e.accountId);
      }
    }
    return s;
  }, [enrollments]);

  const sourceAccounts = useMemo(() => {
    if (showAll) return allAccounts || [];
    return eligibility?.accounts || [];
  }, [showAll, allAccounts, eligibility]);

  const filteredAccounts = useMemo(() => {
    if (!q.trim()) return sourceAccounts;
    const needle = q.toLowerCase();
    return sourceAccounts.filter((a) =>
      (a.name || '').toLowerCase().includes(needle) ||
      (a.dbn || '').toLowerCase().includes(needle),
    );
  }, [sourceAccounts, q]);

  // Selectable = not currently enrolled.
  const selectableAccounts = useMemo(
    () => filteredAccounts.filter((a) => !enrolledAccountIds.has(a.id)),
    [filteredAccounts, enrolledAccountIds],
  );

  const toggle = (aid) => {
    if (enrolledAccountIds.has(aid)) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(aid)) next.delete(aid); else next.add(aid);
      return next;
    });
  };
  const selectAll = () => {
    if (selectableAccounts.length === 0) return;
    if (selected.size === selectableAccounts.length) setSelected(new Set());
    else setSelected(new Set(selectableAccounts.map((r) => r.id)));
  };

  const enroll = async () => {
    setEnrolling(true);
    try {
      const res = await api.post(`/api/admin/outreach/campaigns/${id}/enroll`, {
        accountIds: Array.from(selected),
      });
      setEnrollResult(res);
      setSelected(new Set());
      // Refresh enrollments so the rows we just enrolled flip to "Enrolled".
      const enrRes = await api.get(`/api/admin/outreach/campaigns/${id}/enrollments?limit=500`);
      setEnrollments(enrRes.enrollments || []);
    } catch (err) {
      alert(err?.message || 'Enroll failed');
    } finally { setEnrolling(false); }
  };

  // Auto-enroll is a setting. Toggling it normally just flips the flag.
  // The one wrinkle: when turning it ON with pre-existing manual enrollments,
  // the operator's mental model is "auto = the eligible pool". So we offer
  // to wipe the existing enrollments and re-populate from targeting in one
  // gesture — opt-in via a confirmation dialog so it's not destructive by
  // surprise. Cancelling the dialog leaves existing enrollments alone.
  const requestSetAutoEnroll = (next) => {
    if (next && enrollments.length > 0) {
      setAutoEnrollConfirm({ enrollmentCount: enrollments.length });
      return;
    }
    applyAutoEnroll(next, { wipeExisting: false });
  };

  const applyAutoEnroll = async (next, { wipeExisting = false } = {}) => {
    setSavingAuto(true); setStatusMsg('');
    try {
      let removed = 0;
      if (wipeExisting) {
        const r = await api.delete(`/api/admin/outreach/campaigns/${id}/enrollments`);
        removed = r?.removed ?? 0;
      }
      await api.patch(`/api/admin/outreach/campaigns/${id}`, { autoEnroll: next });
      setCampaign((c) => c ? { ...c, autoEnroll: next } : c);
      if (next && campaign?.status === 'active') {
        // After wiping, immediately re-run so the operator sees the new
        // enrolled list right away (matches the "auto = eligible pool"
        // mental model). If campaign isn't active, defer to next cron run.
        try {
          const r = await api.post(`/api/admin/outreach/campaigns/${id}/run-now`, {});
          await reload();
          const removedNote = wipeExisting ? ` Removed ${removed} prior enrollment${removed === 1 ? '' : 's'}.` : '';
          setStatusMsg(`Auto-enroll on.${removedNote} Enrolled ${r?.enrolled ?? 0} matching account${r?.enrolled === 1 ? '' : 's'}${r?.sent ? `, sent ${r.sent}` : ''}. Future matches are added automatically.`);
        } catch (err) {
          await reload();
          setStatusMsg(`Auto-enroll on, but the immediate enrollment run failed: ${err?.data?.error || err?.message || 'unknown'}. The cron will retry.`);
        }
      } else {
        await reload();
        if (next) {
          const removedNote = wipeExisting ? ` Removed ${removed} prior enrollment${removed === 1 ? '' : 's'}.` : '';
          setStatusMsg(`Auto-enroll on.${removedNote} Nothing will run until the campaign is activated (currently ${campaign?.status || 'draft'}).`);
        } else {
          setStatusMsg('Auto-enroll off. Existing enrollments are kept; new matching accounts will not be added automatically.');
        }
      }
    } catch (err) {
      setStatusMsg(err?.data?.error || err?.message || 'Failed to save');
    } finally {
      setSavingAuto(false);
      setAutoEnrollConfirm(null);
    }
  };

  // Explicit one-shot: enroll all currently matching accounts NOW + send
  // any due step-0 emails. Decoupled from auto-enroll so a user can run
  // it without committing to ongoing auto-enrollment.
  const runNow = async () => {
    if (campaign?.status !== 'active') {
      setStatusMsg(`Run enrollment requires an active campaign. Activate the campaign first (currently ${campaign?.status || 'draft'}).`);
      return;
    }
    setRunning(true); setStatusMsg('');
    try {
      const r = await api.post(`/api/admin/outreach/campaigns/${id}/run-now`, {});
      await reload();
      setStatusMsg(`Run complete. Enrolled ${r?.enrolled ?? 0} new account${r?.enrolled === 1 ? '' : 's'}${r?.sent ? `, sent ${r.sent} email${r.sent === 1 ? '' : 's'}` : ''}.`);
    } catch (err) {
      setStatusMsg(err?.data?.error || err?.message || 'Run failed');
    } finally { setRunning(false); }
  };

  const activateCampaign = async () => {
    try {
      await api.put(`/api/admin/outreach/campaigns/${id}`, { status: 'active' });
      setCampaign((c) => c ? { ...c, status: 'active' } : c);
      setStatusMsg('Campaign activated.');
    } catch (err) {
      setStatusMsg(`Failed to activate: ${err?.data?.error || err?.message || 'unknown error'}`);
    }
  };


  const skippedByReason = useMemo(() => {
    const m = {};
    for (const s of enrollResult?.skipped || []) m[s.reason] = (m[s.reason] || 0) + 1;
    return m;
  }, [enrollResult]);

  const targetingStages = useMemo(() => {
    try { return JSON.parse(campaign?.targetLifecycleStagesJson || '[]'); } catch { return []; }
  }, [campaign]);
  const targetingFilter = useMemo(() => {
    try { return JSON.parse(campaign?.targetAccountFilterJson || '{}'); } catch { return {}; }
  }, [campaign]);
  const targetingRoles = useMemo(() => {
    try {
      const arr = JSON.parse(campaign?.targetContactRolesJson || '[]');
      if (Array.isArray(arr) && arr.length > 0) return arr;
    } catch { /* fall through */ }
    return campaign?.targetContactRole ? [campaign.targetContactRole] : [];
  }, [campaign]);

  // Same staff gate as Outreach.jsx / OutreachDetail.jsx — hooks above must
  // all run before this early return.
  if (!isStaff) {
    return <EmptyState title="Staff only" description="Outreach is restricted to Nexsav staff." />;
  }

  return (
    <div className="max-w-5xl mx-auto">
      <BackLink to={`/admin/outreach/${id}`} className="inline-flex items-center gap-1.5 text-[12px] text-gray-500 hover:text-white mb-4">
        <ArrowLeft className="w-3 h-3" /> Back to campaign
      </BackLink>

      <div className="mb-4 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl md:text-2xl font-semibold text-white tracking-tight">Enroll accounts</h1>
          <p className="text-[13px] text-gray-500 mt-0.5">
            {campaign?.name ? `For "${campaign.name}". ` : ''}
            Targeting defines the eligible pool. Auto-enroll is a setting that controls cron behaviour. Manual selection adds accounts immediately.
          </p>
        </div>
        {campaign?.status && <StatusBadge status={campaign.status} />}
      </div>

      {campaign?.status && campaign.status !== 'active' && (
        <div className="mb-4 rounded-xl border border-amber-500/25 bg-amber-500/[0.06] p-3 flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
            <div className="text-[12px] text-amber-200">
              Campaign is <span className="font-semibold">{campaign.status}</span>. The auto-enroll cron and the send pipeline only run on active campaigns. Activate the campaign when you're ready to start sending.
            </div>
          </div>
          <button
            type="button"
            onClick={activateCampaign}
            className="text-[12px] px-3 py-1.5 rounded-lg border border-amber-500/40 text-amber-100 hover:bg-amber-500/10"
          >
            Activate campaign
          </button>
        </div>
      )}

      {/* Targeting summary */}
      <div className="mb-4 rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="text-[11px] uppercase tracking-wider text-gray-500">Targeting summary</div>
          <a href={`/admin/outreach/${id}`} className="text-[11px] text-[#B0FC38] hover:underline">Edit targeting →</a>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-[12px] text-gray-300">
          <Row label="Stages" value={targetingStages.length ? targetingStages.join(', ') : 'any stage'} />
          <Row label="Borough" value={targetingFilter.borough ? (BOROUGH_LABELS[targetingFilter.borough] || targetingFilter.borough) : 'any'} />
          <Row label="District" value={targetingFilter.district || 'any'} />
          <Row label="Recipient role" value={
            targetingRoles.length
              ? targetingRoles.map((r) => ROLE_LABELS[r] || r).join(', ')
              : <span className="text-amber-400">none, set a role to enable enrollment</span>
          } />
        </div>
        <div className="mt-3 pt-3 border-t border-white/[0.04] text-[12px] text-white">
          <span className="font-semibold">{eligibility?.total ?? '…'}</span>
          <span className="text-gray-500"> account{eligibility?.total === 1 ? '' : 's'} match this targeting · </span>
          <span className="font-semibold">{enrolledAccountIds.size}</span>
          <span className="text-gray-500"> currently enrolled.</span>
        </div>
      </div>

      {/* Auto-enroll setting card */}
      <div className="mb-4 rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <Zap className={`w-4 h-4 mt-0.5 ${campaign?.autoEnroll ? 'text-[#B0FC38]' : 'text-gray-500'}`} />
            <div>
              <div className="text-[13px] font-medium text-white">Auto-enroll matching accounts</div>
              <div className="text-[11.5px] text-gray-400 mt-0.5 leading-relaxed max-w-2xl">
                When the campaign is active, the daily cron will enroll any account that matches Targeting. New schools that match in the future are added automatically.
                <br /><span className="text-gray-500">This is just a setting. Toggling it doesn't activate the campaign or enroll anyone right now. Use "Run enrollment now" below for an immediate one-shot.</span>
              </div>
            </div>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer shrink-0">
            <span className="text-[11px] uppercase tracking-wider text-gray-500">{campaign?.autoEnroll ? 'On' : 'Off'}</span>
            <input
              type="checkbox"
              checked={!!campaign?.autoEnroll}
              disabled={savingAuto || loading}
              onChange={(e) => requestSetAutoEnroll(e.target.checked)}
              className="w-4 h-4 accent-[#B0FC38]"
              data-testid="auto-enroll-toggle"
            />
          </label>
        </div>
        <div className="mt-3 pt-3 border-t border-white/[0.04] flex items-center justify-between flex-wrap gap-2">
          <div className="text-[11.5px] text-gray-500">
            <span className="text-gray-300 font-medium">Run enrollment now</span> · enroll all currently matching accounts immediately and dispatch any due step-0 emails. Works whether auto-enroll is on or off.
          </div>
          <button
            type="button"
            onClick={() => setRunNowConfirm(true)}
            disabled={running || loading || campaign?.status !== 'active'}
            title={campaign?.status !== 'active' ? 'Activate the campaign first' : ''}
            className="text-[12px] px-3 py-1.5 rounded-lg bg-[#B0FC38]/[0.12] border border-[#B0FC38]/30 text-[#D7FF8A] hover:bg-[#B0FC38]/20 disabled:opacity-40"
          >
            <Zap className="w-3 h-3 inline mr-1" /> {running ? 'Running…' : 'Run enrollment now'}
          </button>
        </div>
      </div>

      {statusMsg && (
        <div className="mb-3 text-[12px] text-emerald-300 bg-emerald-500/5 border border-emerald-500/20 rounded-lg px-3 py-1.5">
          {statusMsg}
        </div>
      )}

      {!targetingRoles.length && (
        <div className="mb-4 rounded-xl border border-amber-500/25 bg-amber-500/[0.06] p-3 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
          <div className="text-[12px] text-amber-200">
            No recipient role is set on this campaign. The eligibility query requires at least one role,
            <a href={`/admin/outreach/${id}`} className="underline ml-1">configure targeting</a> before enrolling.
          </div>
        </div>
      )}

      {enrollResult && (
        <div className="mb-4 p-3 rounded-xl border border-emerald-500/20 bg-emerald-500/5 text-[12px] text-emerald-300">
          <div className="font-medium">Enrolled {enrollResult.enrolledCount}, skipped {enrollResult.skippedCount}.</div>
          {enrollResult.skippedCount > 0 && (
            <div className="text-gray-400 mt-1">
              {Object.entries(skippedByReason).map(([reason, n]) => (
                <span key={reason} className="mr-3">{n} {reason.replace('_', ' ')}</span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Eligible / All accounts list */}
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <div>
            <div className="text-[13px] font-medium text-white">
              {showAll ? 'All accounts (override)' : 'Eligible accounts'}
            </div>
            <div className="text-[11px] text-gray-500">
              {showAll
                ? 'Override active: selecting outside the pool will show "ineligible" reasons on enroll.'
                : 'Constrained by Targeting. Enrolled accounts are marked and can\'t be re-selected. Remove them from the campaign detail page if needed.'}
            </div>
          </div>
          <label className="inline-flex items-center gap-2 text-[12px] text-gray-300">
            <input
              type="checkbox"
              checked={showAll}
              onChange={(e) => { setShowAll(e.target.checked); setSelected(new Set()); }}
              className="accent-[#B0FC38]"
            />
            Show all accounts (override)
          </label>
        </div>

        <div className="flex items-center gap-2 mb-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or DBN"
              className="w-full pl-9 pr-3 py-1.5 rounded-lg bg-white/[0.03] border border-white/[0.08] text-[13px] text-white" />
          </div>
        </div>

        <div className="text-[11px] text-gray-500 mb-2">
          {loading
            ? 'Loading…'
            : `${filteredAccounts.length} account${filteredAccounts.length === 1 ? '' : 's'} ${q ? 'match search' : 'in list'} · ${enrolledAccountIds.size > 0 ? `${filteredAccounts.filter((a) => enrolledAccountIds.has(a.id)).length} already enrolled · ` : ''}${selected.size} selected`}
        </div>

        <div className="rounded-lg border border-white/[0.05] bg-white/[0.01] overflow-hidden max-h-[55vh] overflow-y-auto">
          <div className="grid grid-cols-12 gap-3 px-4 py-2 bg-white/[0.02] border-b border-white/[0.05] text-[10px] uppercase tracking-wider text-gray-500 sticky top-0">
            <div className="col-span-1">
              <input type="checkbox" className="accent-[#B0FC38]"
                checked={selectableAccounts.length > 0 && selected.size === selectableAccounts.length}
                onChange={selectAll}
                disabled={selectableAccounts.length === 0}
                title={selectableAccounts.length === 0 ? 'No selectable accounts' : 'Select all (excluding already enrolled)'}
              />
            </div>
            <div className="col-span-6 lg:col-span-5">Name</div>
            <div className="hidden lg:block lg:col-span-2">Stage</div>
            <div className="hidden lg:block lg:col-span-2">Borough · District</div>
            <div className="col-span-5 lg:col-span-2 text-right pr-2">Status</div>
          </div>
          {filteredAccounts.length === 0 && !loading && (
            <div className="px-4 py-6 text-center text-[12px] text-gray-500">
              {showAll ? 'No accounts match your search.' : 'No accounts match the campaign\'s Targeting filters.'}
            </div>
          )}
          {filteredAccounts.map((a) => {
            const isEnrolled = enrolledAccountIds.has(a.id);
            return (
              <label
                key={a.id}
                className={`grid grid-cols-12 gap-3 px-4 py-2 border-b border-white/[0.03] ${
                  isEnrolled ? 'bg-emerald-500/[0.04]' : 'hover:bg-white/[0.02] cursor-pointer'
                }`}
              >
                <div className="col-span-1">
                  <input
                    type="checkbox"
                    className="accent-[#B0FC38]"
                    checked={isEnrolled || selected.has(a.id)}
                    disabled={isEnrolled}
                    onChange={() => toggle(a.id)}
                  />
                </div>
                <div className="col-span-6 lg:col-span-5 text-[13px] text-white truncate">{a.name}</div>
                <div className="hidden lg:block lg:col-span-2 text-[11px] text-gray-400">{a.lifecycleStage}</div>
                <div className="hidden lg:block lg:col-span-2 text-[11px] text-gray-500">
                  {(BOROUGH_LABELS[a.borough || a.typeSchoolBorough] || a.borough || a.typeSchoolBorough || '—')}
                  {' · '}
                  D{a.district || a.typeSchoolDistrict || '—'}
                </div>
                <div className="col-span-5 lg:col-span-2 text-right pr-2">
                  {isEnrolled ? (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300">
                      <Check className="w-3 h-3" /> Enrolled
                    </span>
                  ) : (
                    <span className="text-[10px] text-gray-500 uppercase tracking-wider">—</span>
                  )}
                </div>
              </label>
            );
          })}
        </div>
      </div>

      {selected.size > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-3 px-4 py-2.5 rounded-full border border-white/[0.08] bg-[#0a0a0f]/90 backdrop-blur-md shadow-[0_10px_40px_-10px_rgba(0,0,0,0.9)]">
          <span className="text-[13px] text-gray-300">{selected.size} selected</span>
          <button
            type="button"
            onClick={() => setSelected(new Set())}
            className="text-[11px] text-gray-500 hover:text-white px-1"
            title="Clear selection"
          >
            <X className="w-3 h-3" />
          </button>
          <button onClick={enroll} disabled={enrolling}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] bg-[#B0FC38] hover:bg-[#9BE22E] text-gray-900 disabled:opacity-50">
            <Send className="w-3 h-3" /> {enrolling ? 'Enrolling…' : `Enroll ${selected.size}`}
          </button>
        </div>
      )}

      <ConfirmDialog
        open={!!autoEnrollConfirm}
        title="Replace current enrollments?"
        description={
          autoEnrollConfirm
            ? `Turning auto-enroll on will replace the existing enrollment list with the eligible pool. Your ${autoEnrollConfirm.enrollmentCount} current enrollment${autoEnrollConfirm.enrollmentCount === 1 ? '' : 's'} will be removed, and all accounts that match Targeting will be enrolled${campaign?.status === 'active' ? ' immediately' : ' once the campaign is activated'}. Cancel to leave auto-enroll off.`
            : ''
        }
        confirmLabel="Replace and enable auto-enroll"
        destructive
        onConfirm={async () => { await applyAutoEnroll(true, { wipeExisting: true }); }}
        onClose={() => setAutoEnrollConfirm(null)}
      />
      <ConfirmDialog
        open={runNowConfirm}
        onClose={() => setRunNowConfirm(false)}
        onConfirm={runNow}
        title="Run enrollment + send now?"
        description={
          `Running now will:\n\n` +
          `• Enroll any account that matches Targeting and isn't already enrolled (up to ${eligibility?.total ?? 0} eligible currently, ${enrolledAccountIds.size} already enrolled)\n` +
          `• Send the next-due step email to active enrollments\n\n` +
          `This dispatches real emails through Resend. There is no undo. Make sure the email content has been previewed and the eligible accounts list reviewed.`
        }
        confirmLabel="Run now"
        destructive
      />
    </div>
  );
}

function StatusBadge({ status }) {
  const tones = {
    draft: 'bg-amber-500/10 border-amber-500/30 text-amber-200',
    active: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-200',
    paused: 'bg-gray-500/10 border-gray-500/30 text-gray-300',
    archived: 'bg-white/[0.04] border-white/[0.08] text-gray-500',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider border ${tones[status] || tones.archived}`}>
      {status}
    </span>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline gap-2">
      <div className="text-[10px] uppercase tracking-wider text-gray-500 w-20 shrink-0">{label}</div>
      <div className="text-[12px] text-white truncate">{value}</div>
    </div>
  );
}
